import { XMarkIcon } from "@heroicons/react/24/outline";
import type { ReactNode } from "react";
import type { DataImportDetail } from "../../apiClient/generated";
import { formatDateTime } from "../../utils/dates";
import { CopyButton } from "./CopyButton";
import {
    dataTypeLabel,
    formatDuration,
    importTypeLabel,
    pipelineDuration,
    providerLabel,
    stateLabel,
    summarizeImportErrors,
    timeoutHint,
} from "./pipeline";
import { ReadableValue } from "./ReadableValue";
import { StatusBadge } from "./StatusBadge";
import { StepTimeline } from "./StepTimeline";
import { useDataImportDetail } from "./useDataImportDetail";

const Section = ({
    title,
    aside,
    children,
}: {
    title: string;
    aside?: ReactNode;
    children: ReactNode;
}) => (
    <section className="border-t border-ae-gray-250 pt-4 mt-4">
        <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className="font-bold">{title}</h3>
            {aside && (
                <span className="text-xs text-ae-slate-600 tabular-nums">
                    {aside}
                </span>
            )}
        </div>
        {children}
    </section>
);

const MetaRow = ({
    label,
    children,
}: {
    label: string;
    children: ReactNode;
}) => (
    <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2">
        <dt className="text-ae-slate-600">{label}</dt>
        <dd className="min-w-0">{children}</dd>
    </div>
);

const PipelineSection = ({
    dataImport,
    now,
}: {
    dataImport: DataImportDetail;
    now: Date;
}) => {
    if (!dataImport.state) {
        return (
            <Section title="Pipeline">
                <p className="text-ae-slate-600">
                    This import ran before the state machine, so it has no
                    steps.
                </p>
            </Section>
        );
    }

    const duration = pipelineDuration(dataImport.stateHistory, now);
    return (
        <Section
            title="Pipeline"
            aside={
                duration &&
                `${duration.running ? "Running for" : "Took"} ${formatDuration(duration.seconds)}`
            }
        >
            <StepTimeline steps={dataImport.stateHistory} now={now} />
        </Section>
    );
};

const ErrorsSection = ({ dataImport }: { dataImport: DataImportDetail }) => {
    const summary = summarizeImportErrors(dataImport.importErrors);
    if (!summary) {
        return null;
    }
    const timedOut = dataImport.status === "TIMED_OUT";
    const hint = timeoutHint(summary.actionStatus);

    return (
        <Section title="Errors">
            <div
                className={`rounded-md border p-3 ${
                    timedOut
                        ? "border-orange-200 bg-orange-50"
                        : "border-red-200 bg-red-50"
                }`}
            >
                {(summary.state || hint) && (
                    <p className="mb-2 font-medium">
                        {summary.state && (
                            <>Failed in {stateLabel(summary.state)}. </>
                        )}
                        {hint}
                    </p>
                )}
                <ReadableValue value={summary.errors} />
            </div>
        </Section>
    );
};

interface DataImportDetailViewProps {
    dataImport: DataImportDetail;
    onClose?: () => void;
    now?: Date;
}

/** A data import's pipeline steps, errors and params. */
export const DataImportDetailView = ({
    dataImport,
    onClose,
    now = new Date(),
}: DataImportDetailViewProps) => {
    const dataType = dataTypeLabel(dataImport.importParams);
    const provider = providerLabel(dataImport.importParams);

    return (
        <article className="text-sm text-ae-blue-900" aria-label="Data import">
            <header className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h2 className="text-lg font-medium">
                        {importTypeLabel(dataImport.importType)} import
                        {dataType && (
                            <span className="text-ae-slate-600">
                                {" "}
                                · {dataType}
                            </span>
                        )}
                    </h2>
                    {provider && (
                        <p className="text-ae-slate-600">{provider}</p>
                    )}
                </div>
                {onClose && (
                    <button
                        type="button"
                        className="p-1 rounded hover:bg-slate-200"
                        onClick={onClose}
                        aria-label="Close"
                        title="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                )}
            </header>

            <dl className="mt-3 space-y-1">
                <MetaRow label="Status">
                    <StatusBadge status={dataImport.status} />
                </MetaRow>
                <MetaRow label="Created">
                    {formatDateTime(dataImport.createdAt)}
                    {dataImport.createdByEmail && (
                        <span className="text-ae-slate-600">
                            {" "}
                            by {dataImport.createdByEmail}
                        </span>
                    )}
                </MetaRow>
                <MetaRow label="Updated">
                    {formatDateTime(dataImport.updatedAt)}
                </MetaRow>
                <MetaRow label="ID">
                    <span className="flex items-center">
                        <span className="truncate font-mono text-xs">
                            {dataImport.id}
                        </span>
                        <CopyButton value={dataImport.id} title="Copy ID" />
                    </span>
                </MetaRow>
            </dl>

            <PipelineSection dataImport={dataImport} now={now} />
            <ErrorsSection dataImport={dataImport} />

            <Section title="Import parameters">
                <ReadableValue value={dataImport.importParams} />
            </Section>
        </article>
    );
};

interface DataImportDetailPanelProps {
    dataImportId: string;
    onClose: () => void;
}

/** Loads a data import and shows its detail. */
export const DataImportDetailPanel = ({
    dataImportId,
    onClose,
}: DataImportDetailPanelProps) => {
    const query = useDataImportDetail(dataImportId);

    if (query.isError) {
        return (
            <p className="text-sm text-red-600" role="alert">
                This import could not be loaded.
            </p>
        );
    }
    if (!query.data) {
        return (
            <p className="text-sm text-ae-slate-600" aria-busy="true">
                Loading the import…
            </p>
        );
    }
    return <DataImportDetailView dataImport={query.data} onClose={onClose} />;
};
